import axios from 'axios';
import pako from 'pako';

import {
  NovitusInvoicePayloadDto,
  NovitusInvoicePayloadDtoToJSON,
  NovitusNonFiscalPayloadDto,
  NovitusNonFiscalPayloadDtoToJSON,
  NovitusReceiptPayloadDto,
  NovitusReceiptPayloadDtoToJSON,
} from '../spdCore/autogenerated/spdApiClient';

import {
  getErrorMessage,
  mapDeviceStatusToMessage,
  parsePrinterError,
  PrinterError,
  PrinterStatus,
  PrinterStatusResponse,
} from './fiscalPrinterErrors';

interface TokenData {
  token: string;
  expiresAt: string;
}

interface PrinterConfig {
  apiUrl: string;
}

export interface ServerTokenApi {
  getToken: (
    url: string,
  ) => Promise<{ token: string | null; expiresAt: string | null }>;
  acquireLock: (
    url: string,
    clientId: string,
    ttlSeconds?: number,
  ) => Promise<{ acquired: boolean; lockExpiresAt: string | null }>;
  setToken: (
    url: string,
    clientId: string,
    token: string,
    expiresAt: string,
  ) => Promise<void>;
  releaseLock: (url: string, clientId: string) => Promise<void>;
}

export interface DeviceStatusEvent {
  status: 'OK' | 'ERROR';
  message: string;
}

export interface PrintingResult {
  jpkid: number;
  info?: string | null;
}

export interface PacketObj {
  protocol: 'NOVITUS' | 'XML';
  value: string;
}

export interface DirectioResult {
  packet?: PacketObj;
}

type EDocumentStatus =
  | 'printed_and_invalid_kid'
  | 'not_printed_and_sent'
  | 'not_printed_and_added_to_db'
  | 'printed_and_sent'
  | 'printed_and_added_to_db'
  | 'printed';

interface PrinterApiEDocument {
  status?: EDocumentStatus;
  code?: number;
}

interface PrinterApiError {
  code?: number;
  description?: string;
}

interface PrinterApiRequest {
  id: string;
  status: PrintoutStatus;
  jpkid?: number;
  e_document?: PrinterApiEDocument;
  response?: DirectioResult;
  error?: PrinterApiError;
}

interface PrinterApiResponse {
  request: PrinterApiRequest;
  device?: PrinterStatus;
}

interface TokenApiResponse {
  token: string;
  expiration_date: string;
}

export enum PrinterDocumentType {
  FiscalReport = 1,
  Receipt = 2,
  ReceiptCancelled = 4,
  Invoice = 8,
  InvoiceCancelled = 16,
  DailyReport = 32,
  NonFiscal = 64,
  Event = 128,
}

export interface PrinterDocumentData {
  data: string;
  type: PrinterDocumentType;
}

type DeviceStatusListener = (event: DeviceStatusEvent) => void;

enum PrintoutStatus {
  Stored = 'STORED',
  Queued = 'QUEUED',
  Pending = 'PENDING',
  Done = 'DONE',
  Error = 'ERROR',
  Unknown = 'UNKNOWN',
}

class PrinterService {
  private static readonly CONFIG_KEY = 'printer_config';
  private static readonly UNIQUE_ID_KEY = 'printer_unique_id';
  private static readonly CLIENT_ID_KEY = 'printer_client_id';
  private static readonly TOKEN_LOCK_TTL_SECONDS = 20;
  private static readonly TOKEN_LOCK_MAX_WAIT_ATTEMPTS = 5;
  private static readonly PRINT_LOCK_TTL_SECONDS = 60;
  private static readonly PRINT_LOCK_MAX_WAIT_ATTEMPTS = 20;
  private static readonly LOCK_RETRY_BASE_DELAY_MS = 300;
  private static readonly LOCK_RETRY_MAX_DELAY_MS = 3000;
  private config: PrinterConfig | null = null;
  private token: TokenData | null = null;
  private uniqueId: string | null = null;
  private clientId: string;
  private serverTokenApi: ServerTokenApi | null = null;
  private locked = false;
  private deviceStatusListeners: Set<DeviceStatusListener> = new Set();
  private previousDeviceStatus: PrinterStatus | null = null;

  constructor() {
    this.loadFromStorage();
    this.clientId = this.getOrCreateClientId();
  }

  setServerTokenApi(api: ServerTokenApi): void {
    this.serverTokenApi = api;
  }

  private getOrCreateClientId(): string {
    let id = sessionStorage.getItem(PrinterService.CLIENT_ID_KEY);
    if (!id) {
      id = PrinterService.generateRandomId();
      sessionStorage.setItem(PrinterService.CLIENT_ID_KEY, id);
    }
    return id;
  }

  private static generateRandomId(): string {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  }

  onDeviceStatus(listener: DeviceStatusListener): () => void {
    this.deviceStatusListeners.add(listener);
    return () => this.deviceStatusListeners.delete(listener);
  }

  private notifyDeviceStatus(raw: PrinterStatus): void {
    const isError = raw.status === 'ERROR';
    const message = mapDeviceStatusToMessage(raw);
    const event: DeviceStatusEvent = {
      status: isError ? 'ERROR' : 'OK',
      message: message,
    };
    if (this.previousDeviceStatus !== raw) {
      this.previousDeviceStatus = raw;
      this.deviceStatusListeners.forEach(l => l(event));
    }
  }

  private loadFromStorage(): void {
    try {
      const configStr = localStorage.getItem(PrinterService.CONFIG_KEY);
      if (configStr) {
        this.config = JSON.parse(configStr);
      }

      this.uniqueId =
        localStorage.getItem(PrinterService.UNIQUE_ID_KEY) ?? null;
    } catch (error) {
      console.error('Nie udało się załadować konfiguracji drukarki:', error);
    }
  }

  private saveConfigToStorage(): void {
    if (this.config) {
      localStorage.setItem(
        PrinterService.CONFIG_KEY,
        JSON.stringify(this.config),
      );
    }
  }

  private saveUniqueIdToStorage(): void {
    if (this.uniqueId) {
      localStorage.setItem(PrinterService.UNIQUE_ID_KEY, this.uniqueId);
    }
  }

  setConfig(apiUrl: string): void {
    this.config = { apiUrl };
    this.saveConfigToStorage();
  }

  getConfig(): PrinterConfig | null {
    return this.config;
  }

  clearConfig(): void {
    this.config = null;
    this.uniqueId = null;
    localStorage.removeItem(PrinterService.CONFIG_KEY);
    localStorage.removeItem(PrinterService.UNIQUE_ID_KEY);
  }

  getStoredUniqueId(): string | null {
    return this.uniqueId;
  }

  isConfigured(): boolean {
    return this.config !== null && this.config.apiUrl !== '';
  }

  private isTokenValid(): boolean {
    if (!this.token) {
      return false;
    }
    const buffer = 60000; // 1 minute in milliseconds
    const expiresAt = new Date(this.token.expiresAt).getTime();
    return Date.now() < expiresAt - buffer;
  }

  private async fetchNewToken(): Promise<string> {
    if (!this.isConfigured()) {
      throw new Error('Drukarka nie jest skonfigurowana');
    }

    try {
      const response = await axios.get<TokenApiResponse>(
        `${this.config?.apiUrl}/token`,
      );
      this.token = {
        token: response.data.token,
        expiresAt: response.data.expiration_date,
      };

      return response.data.token;
    } catch (error) {
      const message = getErrorMessage(error);
      throw new Error(message);
    }
  }

  private async refreshToken(): Promise<string> {
    if (!this.token || !this.isConfigured()) {
      throw new Error('Brak tokenu do odświeżenia');
    }

    try {
      const response = await axios.patch<TokenApiResponse>(
        `${this.config?.apiUrl}/token`,
        {},
        {
          headers: {
            Authorization: `Bearer ${this.token.token}`,
          },
        },
      );
      this.token = {
        token: response.data.token,
        expiresAt: response.data.expiration_date,
      };

      return response.data.token;
    } catch (error) {
      const message = getErrorMessage(error);
      throw new Error(message);
    }
  }

  seedToken(token: string, expiresAt: string): void {
    this.token = { token, expiresAt };
  }

  private isServerTokenValid(expiresAt: string | null): boolean {
    if (!expiresAt) {
      return false;
    }
    const buffer = 60000; // 1 minute in milliseconds
    return Date.now() < new Date(expiresAt).getTime() - buffer;
  }

  private async tryServerCall<T>(fn: () => Promise<T>): Promise<T | null> {
    try {
      return await fn();
    } catch (error) {
      console.warn(
        'Koordynacja tokenu drukarki z serwerem nie powiodła się:',
        error,
      );
      return null;
    }
  }

  private async getValidTokenFromDeviceOnly(): Promise<string> {
    if (this.isTokenValid() && this.token) {
      return this.token.token;
    }

    if (this.token) {
      try {
        return await this.refreshToken();
      } catch (error) {
        console.warn(
          'Odświeżenie tokenu nie powiodło się, pobieranie nowego:',
          error,
        );
        return await this.fetchNewToken();
      }
    }

    return await this.fetchNewToken();
  }

  private async runExclusive<T>(
    fn: () => Promise<T>,
    options: {
      ttlSeconds: number;
      maxWaitAttempts: number;
      onExhausted: 'fallback' | 'throw';
    },
  ): Promise<T> {
    const url = this.config?.apiUrl;
    const serverTokenApi = this.serverTokenApi;
    if (!serverTokenApi || !url) {
      return fn();
    }

    if (this.locked) {
      return fn();
    }

    const tryAcquire = () =>
      this.tryServerCall(() =>
        serverTokenApi.acquireLock(url, this.clientId, options.ttlSeconds),
      );

    let acquired = await tryAcquire();
    for (
      let attempt = 0;
      !acquired?.acquired && attempt < options.maxWaitAttempts;
      attempt++
    ) {
      const delay = Math.min(
        PrinterService.LOCK_RETRY_BASE_DELAY_MS * 2 ** attempt,
        PrinterService.LOCK_RETRY_MAX_DELAY_MS,
      );
      await new Promise(resolve => setTimeout(resolve, delay));
      acquired = await tryAcquire();
    }

    if (!acquired?.acquired) {
      if (options.onExhausted === 'throw') {
        throw new Error(
          'Drukarka jest obecnie używana w innym oknie przeglądarki. Spróbuj ponownie za chwilę.',
        );
      }
      return fn();
    }

    this.locked = true;
    try {
      return await fn();
    } finally {
      this.locked = false;
      await this.tryServerCall(() =>
        serverTokenApi.releaseLock(url, this.clientId),
      );
    }
  }

  async getValidToken(): Promise<string> {
    if (this.isTokenValid() && this.token) {
      return this.token.token;
    }

    const url = this.config?.apiUrl;
    const serverTokenApi = this.serverTokenApi;
    if (!serverTokenApi || !url) {
      return this.getValidTokenFromDeviceOnly();
    }

    const serverState = await this.tryServerCall(() =>
      serverTokenApi.getToken(url),
    );
    if (serverState?.token && serverState.expiresAt) {
      this.token = {
        token: serverState.token,
        expiresAt: serverState.expiresAt,
      };
      if (this.isServerTokenValid(serverState.expiresAt)) {
        return serverState.token;
      }
    }

    return this.runExclusive(
      () => this.getValidTokenFromDeviceOnlyAndPublish(),
      {
        ttlSeconds: PrinterService.TOKEN_LOCK_TTL_SECONDS,
        maxWaitAttempts: PrinterService.TOKEN_LOCK_MAX_WAIT_ATTEMPTS,
        onExhausted: 'fallback',
      },
    );
  }

  private async getValidTokenFromDeviceOnlyAndPublish(): Promise<string> {
    const token = await this.getValidTokenFromDeviceOnly();
    return this.publishToken(token);
  }

  private async fetchNewTokenAndPublish(): Promise<string> {
    const token = await this.fetchNewToken();
    return this.publishToken(token);
  }

  private async publishToken(token: string): Promise<string> {
    const url = this.config?.apiUrl;
    const serverTokenApi = this.serverTokenApi;
    if (serverTokenApi && url) {
      await this.tryServerCall(() =>
        serverTokenApi.setToken(
          url,
          this.clientId,
          token,
          this.token!.expiresAt,
        ),
      );
    }
    return token;
  }

  private async executeWithTokenRetry<T>(
    request: (token: string) => Promise<T>,
  ): Promise<T> {
    let token = await this.getValidToken();

    try {
      return await request(token);
    } catch (error) {
      if (error instanceof Error && error.name === 'PrinterError') {
        throw error;
      }

      const axiosError = error as { response?: { status: number } };
      if (axiosError.response?.status === 401) {
        try {
          token = await this.runExclusive(
            () => this.fetchNewTokenAndPublish(),
            {
              ttlSeconds: PrinterService.TOKEN_LOCK_TTL_SECONDS,
              maxWaitAttempts: PrinterService.TOKEN_LOCK_MAX_WAIT_ATTEMPTS,
              onExhausted: 'fallback',
            },
          );
          return await request(token);
        } catch (retryError) {
          const message = getErrorMessage(retryError);
          throw new Error(message);
        }
      }
      throw error;
    }
  }

  private static extractPrintingResult(
    data: PrinterApiResponse,
  ): PrintingResult {
    const req = data.request;
    const jpkid = req?.jpkid ?? 0;
    const edDocumentStatus = req?.e_document?.status;
    let info: string | null = null;
    switch (edDocumentStatus) {
      case 'printed_and_invalid_kid':
        info =
          'E-Paragon nie został wysłany - niepoprawny KID, został wydrukowany.';
        break;
      case 'not_printed_and_added_to_db':
        info =
          'E-Paragon nie został wysłany, został zafiskalizowany i zapisany w bazie danych drukarki.';
        break;
      case 'printed_and_added_to_db':
        info =
          'E-Paragon nie został wysłany, został zafiskalizowany, wydrukowany i zapisany w bazie danych drukarki.';
        break;
    }
    return { jpkid, info };
  }

  private static extractDirectioResult(
    data: PrinterApiResponse,
  ): DirectioResult {
    const packet = data.request?.response?.packet;
    return { packet };
  }

  private async checkPrintingStatus<TResult>(
    endpoint: string,
    printoutId: string,
    token: string,
    extractResult: (data: PrinterApiResponse) => TResult,
  ): Promise<TResult> {
    const maxAttempts = 30;
    const pollInterval = 1000;
    let attempts = 0;
    while (attempts < maxAttempts) {
      const response = await axios.get<PrinterApiResponse>(
        `${this.config?.apiUrl}/${endpoint}/${printoutId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data.device) {
        this.notifyDeviceStatus(response.data.device);
      }

      const status = response.data.request?.status;
      if (status === PrintoutStatus.Done) {
        return extractResult(response.data);
      }

      if (
        status === PrintoutStatus.Error ||
        status === PrintoutStatus.Unknown
      ) {
        const printerError = parsePrinterError(
          response.data as PrinterStatusResponse,
        );
        throw printerError;
      }

      attempts++;
      if (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      }
    }

    throw new Error(
      `Drukowanie nie zostało ukończone w ciągu ${maxAttempts} prób`,
    );
  }

  private async executeConfirmedPrint<TPayload extends object, TResult>(
    endpoint: string,
    payload: TPayload,
    extractResult: (data: PrinterApiResponse) => TResult,
    skipIdentityCheck = false,
  ): Promise<TResult> {
    if (!this.isConfigured()) {
      throw new Error('Drukarka nie jest skonfigurowana');
    }

    return this.runExclusive(
      () =>
        this.runConfirmedPrint(
          endpoint,
          payload,
          extractResult,
          skipIdentityCheck,
        ),
      {
        ttlSeconds: PrinterService.PRINT_LOCK_TTL_SECONDS,
        maxWaitAttempts: PrinterService.PRINT_LOCK_MAX_WAIT_ATTEMPTS,
        onExhausted: 'throw',
      },
    );
  }

  private async runConfirmedPrint<TPayload extends object, TResult>(
    endpoint: string,
    payload: TPayload,
    extractResult: (data: PrinterApiResponse) => TResult,
    skipIdentityCheck: boolean,
  ): Promise<TResult> {
    if (!skipIdentityCheck) {
      await this.verifyPrinterIdentity();
    }

    try {
      return await this.executeWithTokenRetry(async token => {
        const response = await axios.post(
          `${this.config?.apiUrl}/${endpoint}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          },
        );

        const printoutId = response.data.request.id;

        // Step 2: Confirm the printout
        await axios.put(
          `${this.config?.apiUrl}/${endpoint}/${printoutId}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        return await this.checkPrintingStatus(
          endpoint,
          printoutId,
          token,
          extractResult,
        );
      });
    } catch (error) {
      if (error instanceof PrinterError) {
        throw error;
      }
      const message = getErrorMessage(error);
      throw new Error(message);
    }
  }

  async printNonFiscalText(
    payload: NovitusNonFiscalPayloadDto,
  ): Promise<PrintingResult> {
    return this.executeConfirmedPrint(
      'nf_printout',
      NovitusNonFiscalPayloadDtoToJSON(payload),
      PrinterService.extractPrintingResult,
    );
  }

  async printReceipt(
    payload: NovitusReceiptPayloadDto,
  ): Promise<PrintingResult> {
    return this.executeConfirmedPrint(
      'receipt',
      NovitusReceiptPayloadDtoToJSON(payload),
      PrinterService.extractPrintingResult,
    );
  }

  async printInvoice(
    payload: NovitusInvoicePayloadDto,
  ): Promise<PrintingResult> {
    return this.executeConfirmedPrint(
      'invoice',
      NovitusInvoicePayloadDtoToJSON(payload),
      PrinterService.extractPrintingResult,
    );
  }

  async printDailyReport(date: string): Promise<PrintingResult> {
    const payload = {
      daily_report: { date },
    };
    return this.executeConfirmedPrint(
      'daily_report',
      payload,
      PrinterService.extractPrintingResult,
    );
  }

  async getUniqueId(): Promise<string> {
    const result = await this.sendXmlCommand(
      '<pakiet><informacja akcja="pamiec_fiskalna" /></pakiet>',
      true,
    );
    const packet = result.packet;
    if (!packet || packet.protocol !== 'XML') {
      throw new Error('Brak odpowiedzi XML z drukarki');
    }
    const xml = atob(packet.value);
    const parser = new DOMParser();
    const doc = parser.parseFromString(xml, 'application/xml');
    const uniqueId = doc
      .querySelector('informacja')
      ?.getAttribute('numer_unikatowy');
    if (!uniqueId) {
      throw new Error('Brak numer_unikatowy w odpowiedzi drukarki');
    }
    return uniqueId;
  }

  async fetchAndStoreUniqueId(): Promise<void> {
    try {
      this.uniqueId = await this.getUniqueId();
      this.saveUniqueIdToStorage();
    } catch {
      this.uniqueId = null;
      localStorage.removeItem(PrinterService.UNIQUE_ID_KEY);
    }
  }

  private async verifyPrinterIdentity(): Promise<void> {
    const currentId = await this.getUniqueId();
    if (!this.uniqueId) {
      this.uniqueId = currentId;
      this.saveUniqueIdToStorage();
      return;
    }
    if (this.uniqueId !== currentId) {
      throw new Error(
        `Drukarka nie zgadza się z zapisaną (${this.uniqueId}). Podłączona drukarka: ${currentId}`,
      );
    }
  }

  async sendXmlCommand(
    xmlCommand: string,
    skipIdentityCheck = false,
  ): Promise<DirectioResult> {
    const encodedCommand = btoa(xmlCommand);
    const payload = {
      direct_io: { xml_cmd: { base64: encodedCommand } },
    };
    return this.executeConfirmedPrint(
      'direct_io',
      payload,
      PrinterService.extractDirectioResult,
      skipIdentityCheck,
    );
  }

  async sendNovCommand(hexCommand: string): Promise<DirectioResult> {
    const hexPairs = hexCommand.match(/.{1,2}/g);
    if (!hexPairs) {
      throw new Error('Invalid hex format');
    }
    let binary = '';
    for (let i = 0; i < hexPairs.length; i++) {
      binary += String.fromCharCode(parseInt(hexPairs[i], 16));
    }
    const encodedCommand = btoa(binary);
    const payload = {
      direct_io: { nov_cmd: { base64: encodedCommand } },
    };
    return this.executeConfirmedPrint(
      'direct_io',
      payload,
      PrinterService.extractDirectioResult,
      true,
    );
  }

  async getDocumentFromProtectedMemory(
    jpkid: number,
    binary = false,
  ): Promise<PrinterDocumentData> {
    try {
      const command = this.buildNovitusPacket(
        `0@PMI${jpkid}/0/${binary ? 1 : 0}/511/`,
      );
      const result = await this.sendNovCommand(command);
      if (!result.packet?.value) {
        return {
          data: binary ? '' : '{}',
          type: PrinterDocumentType.NonFiscal,
        };
      }
      const data = atob(result.packet.value);
      const firstSlashIndex = data.indexOf('/');
      const secondSlashIndex = data.indexOf('/', firstSlashIndex + 1);
      const documentTypeStr = data.substring(
        firstSlashIndex + 1,
        secondSlashIndex,
      );
      const documentTypeNum = parseInt(documentTypeStr, 10);
      const lastSlashIndex = data.indexOf('\r') + 1;
      const newlineAfterIndex = data.indexOf('\r', lastSlashIndex + 1);
      const base64Str = data
        .substring(lastSlashIndex + 1, newlineAfterIndex)
        .trim();
      if (!base64Str) {
        return { data: '', type: documentTypeNum };
      }

      const bytes = PrinterService.b64urlDecodeBytes(base64Str);
      const decompressed = pako.inflate(bytes);
      if (binary) {
        let binaryStr = '';
        for (let i = 0; i < decompressed.length; i++) {
          binaryStr += String.fromCharCode(decompressed[i]);
        }
        return { data: binaryStr, type: documentTypeNum };
      }
      const decoder = new TextDecoder('windows-1250');
      const jsonString = decoder.decode(decompressed);
      if (jsonString.trimStart().startsWith('{')) {
        return { data: jsonString, type: documentTypeNum };
      }
      return {
        data: PrinterService.decodeJwsPayload(jsonString),
        type: documentTypeNum,
      };
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(
          `Błąd podczas pobierania dokumentu z pamięci drukarki: ${error.message}`,
        );
      }
      throw error;
    }
  }

  async getLastJpkId(): Promise<number> {
    const command = this.buildNovitusPacket('210#s');
    const result = await this.sendNovCommand(command);
    if (!result.packet?.value) {
      throw new Error('Brak danych w odpowiedzi drukarki');
    }
    // Response: <ESC>P210#X0000;ECK...<CR>540;1;6406;6537;EF<ESC>\
    // The last jpkid is the first token after \r
    const data = atob(result.packet.value);
    const crIndex = data.indexOf('\r');
    if (crIndex === -1) {
      throw new Error('Nieoczekiwany format odpowiedzi drukarki');
    }
    const lastJpkId = parseInt(data.substring(crIndex + 1).split(';')[0], 10);
    if (isNaN(lastJpkId)) {
      throw new Error('Nie można odczytać ID JPK z drukarki');
    }
    return lastJpkId;
  }

  private readonly PACKET_START = [0x1b, 0x50]; // <ESC>P
  private readonly PACKET_END = [0x1b, 0x5c]; // <ESC>\

  private calculateChecksum(data: Uint8Array): number {
    let chk = 0xff;
    for (let i = 0; i < data.length; i++) {
      chk ^= data[i];
    }
    return chk;
  }

  buildNovitusPacket(commandAndData: string): string {
    // Process input: replace placeholders with actual bytes
    let processedCommand = commandAndData;
    processedCommand = processedCommand.replace(/<CR>/gi, '\r');
    processedCommand = processedCommand.replace(/<LF>/gi, '\n');
    processedCommand = processedCommand.replace(/<ESC>/gi, '\x1b');

    // Convert command+data string to bytes
    const encoder = new TextEncoder();
    const commandBytes = encoder.encode(processedCommand);

    // Calculate checksum
    const checksum = this.calculateChecksum(commandBytes);
    const checksumHex = checksum.toString(16).toUpperCase().padStart(2, '0');
    const checksumBytes = encoder.encode(checksumHex);

    // Build complete packet: ESC P + command + checksum + ESC \
    const packet = new Uint8Array(
      this.PACKET_START.length +
        commandBytes.length +
        checksumBytes.length +
        this.PACKET_END.length,
    );

    let offset = 0;
    packet.set(this.PACKET_START, offset);
    offset += this.PACKET_START.length;
    packet.set(commandBytes, offset);
    offset += commandBytes.length;
    packet.set(checksumBytes, offset);
    offset += checksumBytes.length;
    packet.set(this.PACKET_END, offset);

    // Convert to hex string
    return Array.from(packet)
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  private static b64urlDecodeBytes(s: string): Uint8Array {
    const padded = s + '='.repeat((4 - (s.length % 4)) % 4);
    const base64 = padded.replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  private static decodeJwsPayload(jws: string): string {
    const parts = jws.trim().split('.');
    if (parts.length !== 3) {
      throw new Error('Not a valid JWS compact serialization');
    }

    const headerBytes = PrinterService.b64urlDecodeBytes(parts[0]);
    const header = JSON.parse(new TextDecoder().decode(headerBytes)) as Record<
      string,
      unknown
    >;

    let payloadBytes = PrinterService.b64urlDecodeBytes(parts[1]);

    const meta = header['jpkmetadata'];
    if (typeof meta === 'string') {
      const metaObj = JSON.parse(
        new TextDecoder().decode(PrinterService.b64urlDecodeBytes(meta)),
      ) as Record<string, unknown>;
      if (metaObj['compression'] === 'DEFLATE') {
        payloadBytes = pako.inflateRaw(payloadBytes);
      }
    }

    return new TextDecoder('utf-8').decode(payloadBytes);
  }

  decodeNovitusResponse(base64Response: string): { hex: string; text: string } {
    const bytes = Uint8Array.from(atob(base64Response), c => c.charCodeAt(0));

    // Hex representation
    const hex = Array.from(bytes)
      .map(b => b.toString(16).padStart(2, '0'))
      .join(' ');

    // Text representation (printable ASCII where possible, hex for control chars)
    let text = '';
    for (let i = 0; i < bytes.length; i++) {
      const byte = bytes[i];
      if (byte >= 32 && byte <= 126) {
        // Printable ASCII
        text += String.fromCharCode(byte);
      } else if (byte === 0x1b) {
        text += '<ESC>';
      } else if (byte === 0x0a) {
        text += '<LF>\n';
      } else if (byte === 0x0d) {
        text += '<CR>';
      } else {
        text += `<0x${byte.toString(16).padStart(2, '0').toUpperCase()}>`;
      }
    }

    return { hex, text };
  }
}

export { PrinterService };
export const printerService = new PrinterService();
